import { skipToken } from '@reduxjs/toolkit/query';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useRef, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useGetContactByIdQuery } from 'store/api/contacts';
import { ContactMinDto } from 'types/contacts';
import { FeatureFlag } from 'types/features';
import { InvoiceForm } from 'types/invoices';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { mapContactToInvoiceRecipientFields } from '../util/InvoicesPageUtil';

type RecipientFields = ReturnType<typeof mapContactToInvoiceRecipientFields>;

type Params = {
    formMethods: UseFormReturn<InvoiceForm>;
    persistedInvoiceForm: InvoiceForm | undefined;
    isSavingInvoice: boolean;
    saveRecipients: () => void;
    /**
     * A card is open in edit, its fields waiting for its Save: nothing saves behind it, so a payer picked there takes
     * its contact's recipients only once the card has saved it, and one put back by Cancel takes none.
     */
    isHeld?: boolean;
};

const EMPTY_RECIPIENT_FIELDS: RecipientFields = {
    primaryRecipientParty: null,
    primaryRecipientAdditionalEmails: null,
    secondaryRecipientParties: [],
    secondaryRecipientAdditionalEmails: null,
};

const recipientPartyIds = (parties: ContactMinDto[] | null | undefined) => (parties ?? []).map(({ id }) => id);

const hasSameRecipients = (a: RecipientFields, b: RecipientFields) =>
    (a.primaryRecipientParty?.id ?? null) === (b.primaryRecipientParty?.id ?? null) &&
    (a.primaryRecipientAdditionalEmails || null) === (b.primaryRecipientAdditionalEmails || null) &&
    (a.secondaryRecipientAdditionalEmails || null) === (b.secondaryRecipientAdditionalEmails || null) &&
    isEqual(recipientPartyIds(a.secondaryRecipientParties), recipientPartyIds(b.secondaryRecipientParties));

/**
 * Repopulates the invoice recipient fields from the payer's contact card when the payer changes.
 * The payer picker option is a min DTO without the recipient block, so the full contact is fetched
 * for the pending payer and the fields are filled (and persisted) once it arrives.
 *
 * The trigger is a payer transition, tracked in a ref. Arming on "form payer differs from the last
 * loaded invoice payload" instead re-arms on every refetch for as long as the two disagree — which
 * is indefinitely when the payer's own auto-save is rejected by form validation, turning a payer
 * change into an endless save loop.
 *
 * Tenants with the INVOICE_PAYER_CHANGE_KEEPS_RECIPIENTS flag keep whatever recipients the invoice
 * already has across payer changes, so the hook stays inert for them.
 */
export const useInvoiceRecipientsFromPayer = ({
    formMethods,
    persistedInvoiceForm,
    isSavingInvoice,
    saveRecipients,
    isHeld = false,
}: Params) => {
    const { control, getValues, setValue, formState } = formMethods;
    const { defaultValues, dirtyFields } = formState;
    const [clientParty] = useWatch({ control, name: ['clientParty'] });
    const payerId = clientParty?.id ?? null;
    const keepsRecipientsOnPayerChange = useIsFeatureEnabled(FeatureFlag.INVOICE_PAYER_CHANGE_KEEPS_RECIPIENTS);

    const [pendingPayerId, setPendingPayerId] = useState<number | null>(null);
    // Stays `undefined` until the form receives its first payload, whose payer needs no repopulation.
    const populatedPayerIdRef = useRef<number | null | undefined>(undefined);

    const { currentData: pendingPayerContact } = useGetContactByIdQuery(pendingPayerId?.toString() ?? skipToken);

    const applyToPristineFields = useCallback(
        (fields: RecipientFields, shouldDirty: boolean) => {
            if (!dirtyFields?.primaryRecipientParty) {
                setValue('primaryRecipientParty', fields.primaryRecipientParty, { shouldDirty });
            }
            if (!dirtyFields?.primaryRecipientAdditionalEmails) {
                setValue('primaryRecipientAdditionalEmails', fields.primaryRecipientAdditionalEmails, { shouldDirty });
            }
            if (!dirtyFields?.secondaryRecipientParties) {
                setValue('secondaryRecipientParties', fields.secondaryRecipientParties, { shouldDirty });
            }
            if (!dirtyFields?.secondaryRecipientAdditionalEmails) {
                setValue('secondaryRecipientAdditionalEmails', fields.secondaryRecipientAdditionalEmails, {
                    shouldDirty,
                });
            }
        },
        [dirtyFields, setValue]
    );

    useEffect(() => {
        if (keepsRecipientsOnPayerChange || isEmpty(defaultValues) || populatedPayerIdRef.current === payerId) {
            return;
        }
        const isFirstPayload = populatedPayerIdRef.current === undefined;
        if (isHeld && !isFirstPayload) {
            return;
        }
        populatedPayerIdRef.current = payerId;
        if (isFirstPayload) {
            return;
        }
        // eslint-disable-next-line react-hooks-js/set-state-in-effect -- arms the payer contact fetch; the trigger is a payer transition, not derivable state
        setPendingPayerId(payerId);
        applyToPristineFields(EMPTY_RECIPIENT_FIELDS, false);
    }, [keepsRecipientsOnPayerChange, payerId, defaultValues, applyToPristineFields, isHeld]);

    useEffect(() => {
        if (
            keepsRecipientsOnPayerChange ||
            !pendingPayerContact ||
            pendingPayerContact.id !== pendingPayerId ||
            isSavingInvoice ||
            isHeld
        ) {
            return;
        }
        // eslint-disable-next-line react-hooks-js/set-state-in-effect -- disarms the payer contact fetch once its result has been applied
        setPendingPayerId(null);
        const recipientFields = mapContactToInvoiceRecipientFields(pendingPayerContact);
        // shouldDirty keeps the populated values safe from the `values` sync until the save below lands.
        applyToPristineFields(recipientFields, true);
        if (persistedInvoiceForm && !hasSameRecipients(getValues(), persistedInvoiceForm)) {
            saveRecipients();
        }
    }, [
        keepsRecipientsOnPayerChange,
        pendingPayerContact,
        pendingPayerId,
        isSavingInvoice,
        isHeld,
        persistedInvoiceForm,
        applyToPristineFields,
        getValues,
        saveRecipients,
    ]);
};
